/**
 * KCIS Ecosystem Homepage — `/`
 *
 * Public gateway that routes visitors to the four confirmed working
 * modules in the KCIS ecosystem. NOT the clinical assessment landing
 * page — that experience is preserved at `/clinic`.
 *
 * Design tokens: white / soft neutral background, KCIS purple, silver
 * accents, restrained green. No gold. Fully responsive.
 */
import React from "react";
import { Link } from "react-router-dom";
import { TopNav, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, ClipboardList } from "lucide-react";

const CARDS = [
  {
    key: "wellness",
    icon: ClipboardList,
    title: "Wellness Assessment",
    desc: "Complete a guided hair, scalp, and functional wellness assessment.",
    to: "/intake",
    cta: "Start Assessment",
    accent: "#4A1D5E",
  },
  {
    key: "deepdive",
    icon: ClipboardList,
    title: "Clinical Deep Dive",
    desc: "A deeper clinical assessment for comprehensive review with your practitioner.",
    to: "/clinic/deep-dive",
    cta: "Start Clinical Deep Dive",
    accent: "#4A1D5E",
  },
];

export default function EcosystemHome() {
  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="ecosystem-home">
      <TopNav
        right={
          <>
            <Link to="/admin/login">
              <Button variant="ghost" className="rounded-full text-slate-700 hover:text-purple-900" data-testid="nav-sign-in">
                Practitioner / Staff Sign In
              </Button>
            </Link>
          </>
        }
      />

      {/* Hero */}
      <section className="bg-gradient-to-b from-white via-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16 text-center">
          <div className="kcis-overline text-purple-900 mb-4">KISSED Trichology &amp; Functional Wellness Clinic</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900 max-w-4xl mx-auto">
            One intelligent system.
            <br />
            <span className="text-purple-900">Multiple ways to move forward.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
            KISSED Clinic OS connects your clinic's wellness assessments, clinical
            review, personalized protocols, reports, and follow-up in one secure system.
          </p>
        </div>
      </section>

      {/* Ecosystem cards */}
      <section className="flex-1">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid gap-6 sm:grid-cols-2">
            {CARDS.map((c) => {
              const Icon = c.icon;
              return (
                <div
                  key={c.key}
                  data-testid={`eco-card-${c.key}`}
                  className="rounded-2xl bg-white p-8 shadow-sm hover:shadow-lg transition-shadow border border-slate-100"
                  style={{ borderTop: `3px solid ${c.accent}` }}
                >
                  <div
                    className="h-11 w-11 rounded-xl flex items-center justify-center mb-4"
                    style={{ background: "#F3E8FF", color: c.accent }}
                  >
                    <Icon className="h-5 w-5" />
                  </div>
                  <h2 className="font-display text-2xl text-slate-900 tracking-tight">{c.title}</h2>
                  <p className="mt-2 text-sm text-slate-600 leading-relaxed">{c.desc}</p>
                  <div className="mt-5 flex flex-wrap gap-2">
                    {c.primaryTo ? (
                      <>
                        <Link to={c.primaryTo}>
                          <Button
                            className="rounded-full text-white px-5"
                            style={{ background: c.accent }}
                            data-testid={`eco-btn-${c.key}-primary`}
                          >
                            {c.primaryCta} <ArrowRight className="ml-1.5 h-4 w-4" />
                          </Button>
                        </Link>
                        <Link to={c.secondaryTo}>
                          <Button
                            variant="ghost"
                            className="rounded-full text-purple-900 hover:bg-purple-50"
                            data-testid={`eco-btn-${c.key}-secondary`}
                          >
                            {c.secondaryCta}
                          </Button>
                        </Link>
                      </>
                    ) : (
                      <Link to={c.to}>
                        <Button
                          className="rounded-full text-white px-5"
                          style={{ background: c.accent }}
                          data-testid={`eco-btn-${c.key}`}
                        >
                          {c.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-14 text-xs text-slate-400" data-testid="staff-access">
            Staff or administrator?{" "}
            <Link to="/admin/login" className="text-purple-900 hover:underline">
              Sign in
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
